/**
 * Anualidades de reajuste (tabela contract_adjustments do backend).
 *
 * Espelho de src/contract-adjustments/adjustment-rules.ts do backend: estados da
 * anualidade, espécies de data-base e índices. O cálculo (índice, retroativo,
 * dinheiro em risco) NÃO mora aqui: vem pronto do backend. Este arquivo só sabe
 * exibir e decidir o que é "pendente".
 */

import { fmtDate, getTodayStr } from './formatters';

export type AdjustmentStatus =
  | 'devida'
  | 'ressalvada'
  | 'pedida'
  | 'deferida'
  | 'aplicada'
  | 'renunciada'
  | 'nao_aplicavel';

type StatusMeta = { label: string; badge: string; hint: string };

export const ADJUSTMENT_STATUS_META: Record<AdjustmentStatus, StatusMeta> = {
  devida: {
    label: 'Devida',
    badge: 'bg-red-500/20 text-red-400',
    hint: 'Interregno fechou e ninguém pediu ainda. Cada competência faturada sai a menos.',
  },
  ressalvada: {
    label: 'Ressalvada',
    badge: 'bg-orange-500/20 text-orange-400',
    hint: 'O órgão já foi comunicado por escrito de que esta anualidade existe e o direito ficou ressalvado. Falta o ofício com o percentual, invocando o anterior.',
  },
  pedida: {
    label: 'Pedida',
    badge: 'bg-amber-500/20 text-amber-400',
    hint: 'Ofício assinado, aguardando o órgão. Continuar faturando o valor antigo.',
  },
  deferida: {
    label: 'Deferida',
    badge: 'bg-blue-500/20 text-blue-400',
    hint: 'Órgão apostilou/aditivou. Falta aplicar no cadastro e cobrar o retroativo.',
  },
  aplicada: {
    label: 'Aplicada',
    badge: 'bg-emerald-500/20 text-emerald-400',
    hint: 'Valor novo em vigor e complementar gerada. Ciclo fechado.',
  },
  renunciada: {
    label: 'Renunciada',
    badge: 'bg-slate-700 text-slate-300',
    hint: 'Abrimos mão desta anualidade por escrito. Evitar: renúncia custa por mês, para sempre.',
  },
  nao_aplicavel: {
    label: 'Não aplicável',
    badge: 'bg-slate-700 text-slate-400',
    hint: 'Não cabe cobrar (decisão registrada na nota, ou o instrumento a exclui).',
  },
};

/** Ordem das seções da tela: primeiro o que é dinheiro na mesa. */
export const ADJUSTMENT_STATUS_ORDER: AdjustmentStatus[] = [
  'devida',
  'ressalvada',
  'pedida',
  'deferida',
  'aplicada',
  'nao_aplicavel',
  'renunciada',
];

export const ADJUSTMENT_PENDING_STATUSES: AdjustmentStatus[] = ['devida', 'ressalvada', 'pedida', 'deferida'];

/**
 * Anualidade cujo aniversário ainda não chegou. NÃO é um estado guardado: é
 * função da data, e guardar viraria mentira no dia seguinte.
 *
 * ⛔ Por que a tela separa (22/09/2026). O grupo "Devida" promete "interregno
 * fechou e ninguém pediu ainda", e o CISNOP caía nele com aniversário só em
 * 21/10/2026 e R$ 0,00 em risco. Vencida e parada exige ofício hoje; a vencer
 * não exige nada — são urgências opostas no mesmo balde vermelho.
 */
export function isAVencer(anniversaryDate?: string | null, today?: string): boolean {
  if (!anniversaryDate) return false;
  // Data de hoje vem de formatters, nunca montada aqui (regra do CLAUDE.md).
  return String(anniversaryDate).slice(0, 10) > (today || getTodayStr());
}

/**
 * Chave de SEÇÃO da tela. É o estado guardado, mais o grupo derivado `a_vencer`,
 * que não existe no banco e não deve existir: estado que muda sozinho com o
 * calendário não se grava.
 */
export type AdjustmentGroupKey = AdjustmentStatus | 'a_vencer';

/** Só o que ainda não foi pedido muda de seção por causa da data. */
const ANTES_DO_PEDIDO: AdjustmentStatus[] = ['devida', 'ressalvada'];

export function adjustmentGroupKey(
  a: { status: AdjustmentStatus | string; anniversaryDate?: string | null },
  today?: string,
): AdjustmentGroupKey {
  if (ANTES_DO_PEDIDO.includes(a.status as AdjustmentStatus) && isAVencer(a.anniversaryDate, today)) return 'a_vencer';
  return a.status as AdjustmentGroupKey;
}

export const ADJUSTMENT_GROUP_META: Record<AdjustmentGroupKey, StatusMeta> = {
  ...ADJUSTMENT_STATUS_META,
  a_vencer: {
    label: 'A vencer',
    badge: 'bg-sky-500/20 text-sky-400',
    hint: 'O aniversário ainda não chegou. Nada a pedir e nada a cobrar; a linha entra em Devida no dia.',
  },
};

/** Ordem das seções abertas, da mais urgente para a que não exige nada hoje. */
export const ADJUSTMENT_OPEN_GROUPS: AdjustmentGroupKey[] = [
  'devida',
  'ressalvada',
  'pedida',
  'deferida',
  'a_vencer',
];

export function isPendingAdjustment(status?: string | null): boolean {
  return ADJUSTMENT_PENDING_STATUSES.includes(status as AdjustmentStatus);
}

export function adjustmentStatusMeta(status?: string | null): StatusMeta {
  return ADJUSTMENT_STATUS_META[status as AdjustmentStatus] || ADJUSTMENT_STATUS_META.devida;
}

export const ADJUSTMENT_INDEX_OPTIONS = ['IPCA', 'INPC', 'IGPM', 'IST', 'ICTI'];

/** Espécie da data-base, lida no documento. */
export const ADJUSTMENT_BASE_KIND_OPTIONS: Array<{ value: string; label: string; short: string; hint: string }> = [
  { value: 'orcamento_estimado', label: 'Orçamento estimado', short: 'orçamento', hint: 'Data do orçamento estimado da licitação. É a mais comum nos TR.' },
  { value: 'assinatura', label: 'Assinatura do contrato', short: 'assinatura', hint: 'Só vale quando o instrumento diz, ou quando é silente (regra da casa).' },
  { value: 'proposta', label: 'Data da proposta', short: 'proposta', hint: 'Um ano da apresentação da proposta (ex.: IPHAN, TR 7.20).' },
  { value: 'vigencia', label: 'Início da vigência', short: 'vigência', hint: 'Aniversário do início da vigência contratual.' },
  { value: 'silente', label: 'Silente (nenhum instrumento fixa)', short: 'silente', hint: 'Contrato, edital e aditivos não fixam índice nem data-base. Direito pela Lei 10.192/2001, art. 3º.' },
];

export function baseKindMeta(value?: string | null) {
  return ADJUSTMENT_BASE_KIND_OPTIONS.find((o) => o.value === value) || null;
}

/** Nota que o cron grava quando o índice definitivo saiu diferente do pedido. */
export const DEFINITIVE_MISMATCH_PREFIX = '⚠️ DEFINITIVO DIFERE';

export function hasDefinitiveMismatch(adj: { note?: string | null }): boolean {
  return !!adj?.note && String(adj.note).startsWith(DEFINITIVE_MISMATCH_PREFIX);
}

/** 0.043915 → "4,3915%"; 0.0439 → "4,39%". Mínimo de 2 casas, sem zeros à direita. */
export function fmtPct(factor: number | string | null | undefined): string {
  if (factor === null || factor === undefined || factor === '') return '—';
  const pct = Number(factor) * 100;
  if (!Number.isFinite(pct)) return '—';
  const s = pct.toFixed(6).replace(/0+$/, '');
  const [i, d = ''] = s.split('.');
  return `${i},${d.padEnd(2, '0')}%`;
}

export function fmtBRL(v: number | string | null | undefined): string {
  const n = Number(v);
  if (v === null || v === undefined || !Number.isFinite(n)) return '—';
  return 'R$ ' + n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** '2026-07-31' ou Date → "jul/26". */
export function fmtMonthShort(d: any): string {
  const s = d ? String(d).slice(0, 7) : '';
  const [y, m] = s.split('-').map(Number);
  if (!y || !m) return '—';
  return `${MESES[m - 1]}/${String(y).slice(2)}`;
}

/**
 * Anualidade pendente que a fatura já deveria refletir.
 *
 * Faturamento é postecipado: a fatura da competência N cobra o serviço de N-1. A
 * comparação usa o período de serviço (periodStart/periodEnd) quando existe; sem
 * ele, o mês anterior à competência. Devolve a diferença mensal entre o que foi
 * faturado (valor "antes" da anualidade mais antiga pendente) e o que deveria ser
 * (valor "depois" da mais recente) — só para o selo, sem calcular retroativo.
 */
export function pendingAdjustmentForInvoice(
  contract: { adjustments?: any[] } | null | undefined,
  invoice: { periodStart?: any; periodEnd?: any; referenceMonth?: string },
): { adjustments: any[]; monthlyDiff: number; latest: any } | null {
  const list = (contract?.adjustments || []).filter((a: any) => isPendingAdjustment(a.status) && a.effectsFrom);
  if (!list.length) return null;

  let serviceEnd: string | null = null;
  if (invoice.periodEnd) serviceEnd = String(invoice.periodEnd).slice(0, 10);
  else if (invoice.referenceMonth) {
    const [y, m] = String(invoice.referenceMonth).split('-').map(Number);
    if (y && m) {
      const prev = new Date(Date.UTC(y, m - 1, 0)); // último dia do mês anterior
      serviceEnd = prev.toISOString().slice(0, 10);
    }
  }
  if (!serviceEnd) return null;

  const applicable = list
    .filter((a: any) => String(a.effectsFrom).slice(0, 10) <= serviceEnd!)
    .sort((a: any, b: any) => (String(a.anniversaryDate) < String(b.anniversaryDate) ? -1 : 1));
  if (!applicable.length) return null;

  const first = applicable[0];
  const latest = applicable[applicable.length - 1];
  const before = Number(first.amountBefore);
  const after = Number(latest.amountAfter);
  if (!Number.isFinite(before) || !Number.isFinite(after)) return { adjustments: applicable, monthlyDiff: 0, latest };
  return { adjustments: applicable, monthlyDiff: Math.round((after - before) * 100) / 100, latest };
}

/**
 * Selo "reajuste aplicado depois desta fatura" — o caso OPOSTO do pendente.
 *
 * A conta NÃO mora aqui: vem pronta em `reajusteNaoCobrado`, na listagem de
 * GET /invoices (backend PR #253). Não recalcular comparando o MRR da fatura com
 * o `mrr` atual do contrato: dá falso positivo em todo contrato que reajustou
 * DEPOIS (CIUENP: inventava R$ 854,93 por fatura correta). A comparação certa é
 * contra o valor que valia no período, dia a dia, e só o backend tem isso.
 *
 * Ausência da chave = fatura em ordem, nada a mostrar (nunca "zero").
 *
 * ⚠️ O verbo muda com `jaPaga`: fatura aberta se CORRIGE; fatura paga só se
 * resolve com FATURA COMPLEMENTAR. Selo igual nos dois casos mandaria alguém
 * editar fatura paga.
 */
export type SituacaoCobertura = 'sem_complementar' | 'complementar_curta' | 'rascunho' | 'cancelada';

/**
 * ⚠️ `situacao`/`complementar` (backend PR #254): complementar em RASCUNHO não
 * cobre o buraco. Antes o selo sumia no minuto em que o rascunho nascia, que é
 * justamente o de maior risco de esquecer (COM8DN, FAT202609147, 29/09/2026).
 * Com rascunho o selo não some: vira "complementar em rascunho FAT…: R$ X a
 * emitir". Cancelada reabre o buraco e volta ao texto de sempre.
 * Sem `situacao` (backend anterior ao #254) o comportamento é o de antes.
 */
export function seloReajusteNaoCobrado(
  invoice: { reajusteNaoCobrado?: any },
): { texto: string; titulo: string; jaPaga: boolean; situacao: SituacaoCobertura | null } | null {
  const r = invoice?.reajusteNaoCobrado;
  const falta = Number(r?.falta);
  if (!r || !Number.isFinite(falta) || falta <= 0.005) return null;
  const jaPaga = !!r.jaPaga;
  const situacao: SituacaoCobertura | null =
    ['sem_complementar', 'complementar_curta', 'rascunho', 'cancelada'].includes(r.situacao) ? r.situacao : null;
  const comp = r.complementar && typeof r.complementar === 'object' ? r.complementar : null;
  const refComp = comp?.code ? ` ${comp.code}` : comp?.id ? ` #${comp.id}` : '';
  const anualidade = r.sequence ? `${r.sequence}ª anualidade` : 'anualidade';
  const efeitos = r.effectsFrom ? `, efeitos desde ${fmtDate(r.effectsFrom)}` : '';
  const acao = jaPaga
    ? 'A fatura já está paga: o acerto é por FATURA COMPLEMENTAR, não editando esta.'
    : 'A fatura ainda está aberta: dá para corrigir o valor antes de cobrar.';
  const porSituacao: Record<SituacaoCobertura, string> = {
    sem_complementar: acao,
    rascunho: `A complementar${refComp} existe mas está em RASCUNHO: enquanto não for emitida, este valor não foi cobrado. Emita a complementar, não crie outra.`,
    cancelada: `A complementar${refComp} foi CANCELADA, então o valor volta a faltar. ${acao}`,
    complementar_curta: `A complementar${refComp} foi emitida, mas cobre menos do que falta. ${acao}`,
  };
  const titulo = [
    `Reajuste aplicado depois desta fatura (${anualidade}${efeitos}).`,
    `MRR esperado ${fmtBRL(r.mrrEsperado)}, na fatura ${fmtBRL(r.mrrNaFatura)}: faltam ${fmtBRL(falta)}.`,
    r.explicacao ? `Situação: ${r.explicacao}.` : '',
    situacao ? porSituacao[situacao] : acao,
  ].filter(Boolean).join(' ');
  const sufixo = jaPaga ? ' · emitir complementar' : ' · corrigir';
  const texto =
    situacao === 'rascunho' ? `complementar em rascunho${comp?.code ? ` ${comp.code}` : ''}: ${fmtBRL(falta)} a emitir`
    : situacao === 'cancelada' ? `complementar cancelada: faltam ${fmtBRL(falta)}${sufixo}`
    : situacao === 'complementar_curta' ? `complementar a menor: faltam ${fmtBRL(falta)}${sufixo}`
    : `reajuste aplicado depois: faltam ${fmtBRL(falta)}${sufixo}`;
  return { texto, titulo, jaPaga, situacao };
}
